import React, { useEffect, useMemo, useState } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { Link, useNavigate } from "react-router-dom";
import ManagementTableLayout from "../../components/MangementTable";
import InputField from "../../components/InputField";
import SearchIcon from "../../assets/SearchIcon";
import { useSticky } from "react-table-sticky";
import Pagination from "../../components/Pagination";
import { toast } from "react-toastify";
import EditIcon from "../../assets/EditIcon";
import { checkPermissions } from "../../utils/helper";
import useDebounce from "../../hooks/useDebounce";
import UpArrow2 from "../../assets/svg-tsx/UpArrow2";
import { Tooltip } from "antd";
import { getAbandonedCarts } from "../../api/abandonedCart";

const AbandonedList = () => {
  const navigate = useNavigate();
  const [totalCount, setTotalCount] = useState<number>(100);
  const [pageLength, setPageLength] = useState<number>(25);
  const [page, setPage] = useState<any>(1);
  const [loading, setLoading] = useState(true);
  const [inputVal, setInputVal] = useState("");
  const [abandonedCartData, setAbandonedCartData] = useState<Array<any>>([]);
  const [sortBy, setSortBy] = useState("");

  const debouncedInputVal = useDebounce(inputVal, 500);

  //Permissions
  const hasViewPermission = checkPermissions(
    "view_abandoned_cart",
    true,
    toast
  );
  const hasEditPermission = checkPermissions("edit_abandoned_cart");

  useEffect(() => {
    setLoading(true);
    getAbandonedCarts({
      page: page,
      length: pageLength,
      search: debouncedInputVal,
      sort_by: sortBy,
    })
      .then((res) => {
        setAbandonedCartData(res?.data?.data?.carts);
        setTotalCount(res?.data?.data?.total_count);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        // Fallback to mock data if API fails
        setAbandonedCartData([]);
        setLoading(false);
      });
  }, [page, pageLength, debouncedInputVal, sortBy]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      setSortBy("");
    } else {
      setSortBy(field);
    }
  };

  const getSortDirection = (field: string) => {
    if (sortBy === field) return "asc";
    if (sortBy === `-${field}`) return "desc";
    return "none";
  };

  const COLUMN = [
    {
      id: "owner_type",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-start items-center gap-2 px-2  cursor-pointer"
          onClick={() => handleSort("owner_type")}
        >
          <p>Type</p>
        </div>
      ),
      width: 90,
      accessor: "owner_type",
      Cell: ({ value, row }: { value: any; row: any }) => (
        <div className="flex justify-start flex-col py-2 pl-2">
          <p className="">{value || "-"}</p>
        </div>
      ),
    },
    {
      id: "owner",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-start items-center gap-2 px-2 cursor-pointer"
          onClick={() => handleSort("owner")}
        >
          <p>Name</p>
          <div className="w-4 h-4 flex items-center justify-center">
            <UpArrow2
              className={`h-4 w-4 transition-transform ${
                getSortDirection("owner") === "desc"
                  ? "rotate-180"
                  : getSortDirection("owner") === "none"
                  ? "opacity-50"
                  : ""
              }`}
            />
          </div>
        </div>
      ),
      accessor: "owner",
      Cell: ({ value, row }: { value: any; row: any }) => (
        <div className="flex justify-start flex-col py-2 pl-2">
          <Link
            to={`/abandoned-cart/${row.original.id}`}
            className="hover:underline"
          >
            <p className="">{value || "-"}</p>
          </Link>
        </div>
      ),
    },
    {
      id: "days_inactive",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-start items-center gap-2 px-2 cursor-pointer"
          onClick={() => handleSort("days_inactive")}
        >
          <p>Inactive Days</p>
        </div>
      ),
      accessor: "days_inactive",
      Cell: ({ value }: { value: any }) => (
        <div className="flex justify-start items-center">
          <span className={`px-2 py-1 rounded-full `}>{value} days</span>
        </div>
      ),
    },
    {
      id: "last_modified",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-start items-center gap-2 px-2 cursor-pointer"
          onClick={() => handleSort("last_modified")}
        >
          <p>Last Modified</p>
        </div>
      ),
      accessor: "last_modified",
      Cell: ({ value }: { value: any }) => {
        const formattedDate = new Date(value)?.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
        return (
          <div className="flex flex-col pl-2">
            <p className="">{formattedDate || "-"}</p>
          </div>
        );
      },
    },
    {
      id: "total_quantity",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-center items-center gap-2 px-2 cursor-pointer"
          onClick={() => handleSort("total_quantity")}
        >
          <p>Total Quantity</p>
        </div>
      ),
      accessor: "total_quantity",
      Cell: ({ value }: { value: any }) => (
        <div className="pl-2">
          <span className="font-semibold">{value || 0}</span>
        </div>
      ),
    },
    {
      id: "number_of_products",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-center items-center gap-2 px-2 cursor-pointer"
          onClick={() => handleSort("number_of_products")}
        >
          <p>Total Products</p>
        </div>
      ),
      accessor: "number_of_products",
      Cell: ({ value }: { value: any }) => (
        <div className="pl-2">
          <span className="font-semibold">{value || 0}</span>
        </div>
      ),
    },
    {
      id: "total_cart_value",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-center items-center gap-2 px-2 cursor-pointer"
          onClick={() => handleSort("total_cart_value")}
        >
          <p>Total Cart Value</p>
          <div className="w-4 h-4 flex items-center justify-center">
            <UpArrow2
              className={`h-4 w-4 transition-transform ${
                getSortDirection("total_cart_value") === "desc"
                  ? "rotate-180"
                  : getSortDirection("total_cart_value") === "none"
                  ? "opacity-50"
                  : ""
              }`}
            />
          </div>
        </div>
      ),
      accessor: "total_cart_value",
      Cell: ({ value }: { value: any }) => (
        <div className="pl-2">
          <span className="font-semibold">{value || 0}</span>
        </div>
      ),
    },
    {
      id: "project_name",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-center items-center gap-2 px-2 cursor-pointer"
          onClick={() => handleSort("project_name")}
        >
          <p>Project Name</p>
        </div>
      ),
      accessor: "project_name",
      Cell: ({ value }: { value: any }) => (
        <div className="pl-2">
          <span className="font-semibold">{value || "-"}</span>
        </div>
      ),
    },
    {
      id: "project_address",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-center items-center gap-2 px-2 cursor-pointer"
          onClick={() => handleSort("project_address")}
        >
          <p>Project Location</p>
          <div className="w-4 h-4 flex items-center justify-center">
            <UpArrow2
              className={`h-4 w-4 transition-transform ${
                getSortDirection("project_address") === "desc"
                  ? "rotate-180"
                  : getSortDirection("project_address") === "none"
                  ? "opacity-50"
                  : ""
              }`}
            />
          </div>
        </div>
      ),
      accessor: "project_address",
      Cell: ({ value }: { value: any }) => (
        <div className="pl-2">
          <span className="font-semibold">{value || "-"}</span>
        </div>
      ),
    },
    ...(hasEditPermission
      ? [
          {
            id: "actions",
            Header: () => (
              <div className="flex w-full justify-center">Actions</div>
            ),
            width: 100,
            Cell: ({ row }: { row: any }) => (
              <div className=" gap-3">
                {hasEditPermission && (
                  <Tooltip title="View Details">
                    <Link to={`/abandoned-cart/${row.original.id}`}>
                      <div
                        onClick={() => {
                          console.log("View cart details:", row.original.id);
                        }}
                        className="stroke-grey cursor-pointer p-1 rounded-md flex items-center justify-center"
                      >
                        <EditIcon />
                      </div>
                    </Link>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const columns = useMemo(() => COLUMN, [abandonedCartData, sortBy]);
  const data = useMemo(() => abandonedCartData, [abandonedCartData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: page, pageSize: pageLength },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / pageLength),
      useSticky,
    },
    useSortBy,
    usePagination
  );

  const handlePageChange = (page: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (size: any) => {
    setPageLength(size);
  };

  if (!hasViewPermission) {
    navigate("/unauthorized");
  }

  return (
    <div className="h-full">
      <div className="flex flex-row justify-between items-center py-2 w-full px-3">
        <div className="flex gap-4 w-[max-content] my-4">
          <div className="w-[235px]">
            <InputField
              onChange={(e: string) => {
                setInputVal(e);
                if (page !== 1) {
                  setPage(1);
                }
              }}
              placeholder="Search abandoned carts"
              value={inputVal}
              rightIcon={<SearchIcon />}
              withDebounce
            />
          </div>
        </div>
      </div>

      <div className="!h-[calc(100%-20vh)] w-full relative -z-0">
        <ManagementTableLayout
          tableInstance={tableInstance}
          loading={loading}
        />
      </div>
      {loading ? (
        ""
      ) : (
        <div className="w-full">
          <Pagination
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={pageLength}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
    </div>
  );
};

export default AbandonedList;
